import type { Item, Photo } from "@workspace/api-client-react";
import { downloadBlob, itemsToCsv } from "./csv-export";
import { formatMeasurement, type MeasurementUnit } from "./measurements";
import { getPhotoRoleLabel } from "./photo-roles";

const textEncoder = new TextEncoder();

export type BackupProgress = {
  stage: "preparing" | "images" | "archive" | "download";
  completed: number;
  total: number;
  currentLabel?: string;
};

export type BackupImageFailure = {
  itemId: number;
  itemName: string;
  photoId: number;
  role: string;
  reason: string;
};

export class BackupImageError extends Error {
  constructor(public readonly failures: BackupImageFailure[]) {
    super(
      `${failures.length} photo${failures.length === 1 ? "" : "s"} could not be added to the backup.`,
    );
    this.name = "BackupImageError";
  }
}

type ZipEntry = {
  name: string;
  data: Uint8Array;
  crc: number;
};

type ManifestPhoto = {
  id: number;
  role: Photo["role"];
  path: string;
  mediaType?: string;
  originalFilename?: string;
  byteSize?: number;
};

type ManifestItem = Omit<Item, "photos"> & {
  photos: ManifestPhoto[];
  stableItemId: number;
  childStableIds: number[];
  parentSetMemberships: Array<{ stableSetId: number; position: number }>;
};

export function relationshipMetadata(item: Pick<Item, "id" | "children" | "parentSets">) {
  const children = [...(item.children ?? [])].sort(
    (a, b) => a.position - b.position || a.id - b.id,
  );
  const parentSets = [...(item.parentSets ?? [])].sort(
    (a, b) => a.position - b.position || a.id - b.id,
  );
  return {
    stableItemId: item.id,
    childStableIds: children.map((child) => child.id),
    parentSetMemberships: parentSets.map((parent) => ({
      stableSetId: parent.id,
      position: parent.position,
    })),
  };
}

const CRC_TABLE = new Uint32Array(256);
for (let n = 0; n < CRC_TABLE.length; n += 1) {
  let value = n;
  for (let bit = 0; bit < 8; bit += 1) {
    value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  }
  CRC_TABLE[n] = value >>> 0;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dosTimestamp(date: Date): { date: number; time: number } {
  const year = Math.max(1980, date.getFullYear());
  return {
    time:
      (date.getHours() << 11) |
      (date.getMinutes() << 5) |
      Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

function writeUint16(view: DataView, offset: number, value: number) {
  view.setUint16(offset, value, true);
}

function writeUint32(view: DataView, offset: number, value: number) {
  view.setUint32(offset, value, true);
}

function createZip(entries: ZipEntry[], createdAt: Date): Blob {
  const timestamp = dosTimestamp(createdAt);
  const encodedNames = entries.map((entry) => textEncoder.encode(entry.name));
  const localSize = entries.reduce(
    (sum, entry, index) =>
      sum + 30 + encodedNames[index].length + entry.data.length,
    0,
  );
  const centralSize = entries.reduce(
    (sum, _entry, index) => sum + 46 + encodedNames[index].length,
    0,
  );
  const totalSize = localSize + centralSize + 22;
  if (totalSize > 0xffffffff) {
    throw new Error("This collection is too large for a standard ZIP backup.");
  }

  const output = new Uint8Array(totalSize);
  const view = new DataView(output.buffer);
  const offsets: number[] = [];
  let cursor = 0;

  entries.forEach((entry, index) => {
    const name = encodedNames[index];
    offsets.push(cursor);
    writeUint32(view, cursor, 0x04034b50);
    writeUint16(view, cursor + 4, 20);
    writeUint16(view, cursor + 6, 0x0800);
    writeUint16(view, cursor + 8, 0);
    writeUint16(view, cursor + 10, timestamp.time);
    writeUint16(view, cursor + 12, timestamp.date);
    writeUint32(view, cursor + 14, entry.crc);
    writeUint32(view, cursor + 18, entry.data.length);
    writeUint32(view, cursor + 22, entry.data.length);
    writeUint16(view, cursor + 26, name.length);
    writeUint16(view, cursor + 28, 0);
    output.set(name, cursor + 30);
    output.set(entry.data, cursor + 30 + name.length);
    cursor += 30 + name.length + entry.data.length;
  });

  const centralOffset = cursor;
  entries.forEach((entry, index) => {
    const name = encodedNames[index];
    writeUint32(view, cursor, 0x02014b50);
    writeUint16(view, cursor + 4, 20);
    writeUint16(view, cursor + 6, 20);
    writeUint16(view, cursor + 8, 0x0800);
    writeUint16(view, cursor + 10, 0);
    writeUint16(view, cursor + 12, timestamp.time);
    writeUint16(view, cursor + 14, timestamp.date);
    writeUint32(view, cursor + 16, entry.crc);
    writeUint32(view, cursor + 20, entry.data.length);
    writeUint32(view, cursor + 24, entry.data.length);
    writeUint16(view, cursor + 28, name.length);
    writeUint16(view, cursor + 30, 0);
    writeUint16(view, cursor + 32, 0);
    writeUint16(view, cursor + 34, 0);
    writeUint16(view, cursor + 36, 0);
    writeUint32(view, cursor + 38, 0);
    writeUint32(view, cursor + 42, offsets[index]);
    output.set(name, cursor + 46);
    cursor += 46 + name.length;
  });

  writeUint32(view, cursor, 0x06054b50);
  writeUint16(view, cursor + 4, 0);
  writeUint16(view, cursor + 6, 0);
  writeUint16(view, cursor + 8, entries.length);
  writeUint16(view, cursor + 10, entries.length);
  writeUint32(view, cursor + 12, cursor - centralOffset);
  writeUint32(view, cursor + 16, centralOffset);
  writeUint16(view, cursor + 20, 0);

  return new Blob([output.buffer], { type: "application/zip" });
}

function addTextEntry(entries: ZipEntry[], name: string, content: string) {
  const data = textEncoder.encode(content);
  entries.push({ name, data, crc: crc32(data) });
}

export function sanitizeBackupName(value: string): string {
  const normalized = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return normalized || "unnamed";
}

export function extensionFor(contentType: string | null): string {
  const mime = contentType?.split(";", 1)[0].trim().toLowerCase();
  const extensions: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/heic": "heic",
    "image/heif": "heif",
    "application/pdf": "pdf",
  };
  return extensions[mime ?? ""] ?? "img";
}

export function backupAttachmentFilename(
  originalFilename: string | undefined,
  attachmentId: number,
  extension: string,
): string {
  const stem = sanitizeBackupName(
    (originalFilename ?? "attachment").replace(/\.[^.]+$/, ""),
  );
  return `${stem}-${attachmentId}.${extension}`;
}

function markdownValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value).replace(/\|/g, "\\|").replace(/\n/g, "<br>");
}

function buildCollectionMarkdown(
  items: ManifestItem[],
  createdAt: Date,
): string {
  const lines = [
    "# ForgeBook Collection",
    "",
    `Created: ${createdAt.toISOString()}`,
    "",
    `Entries: ${items.length}`,
    "",
  ];

  for (const item of items) {
    const unit: MeasurementUnit = item.preferredDisplayUnit ?? "mm";
    lines.push(
      `## ${item.name}`,
      "",
      `Stable ID: \`${item.id}\``,
      `Item Type: ${item.itemType}`,
      ...(item.itemType === "set"
        ? [
            `Ordered child stable IDs: ${item.childStableIds.length > 0 ? item.childStableIds.map((id) => `\`${id}\``).join(", ") : "—"}`,
          ]
        : [
            `Parent Set memberships: ${item.parentSetMemberships.length > 0 ? item.parentSetMemberships.map((membership) => `\`${membership.stableSetId}\` (order ${membership.position})`).join(", ") : "—"}`,
          ]),
      "",
      "### Details",
      "",
      "| Field | Value |",
      "| --- | --- |",
      `| Inventory Status | ${item.inventoryStatus === "sold" ? "Sold" : "In-Inventory"} |`,
      `| Manufacturer | ${markdownValue(item.manufacturer)} |`,
      `| Blade Style | ${markdownValue(item.bladeStyle)} |`,
      `| Handle Material | ${markdownValue(item.handleMaterial)} |`,
      `| Blade Material | ${markdownValue(item.bladeMaterial)} |`,
      `| Generation | ${markdownValue(item.generation)} |`,
      `| Blade Design | ${markdownValue(item.bladeDesign)} |`,
      `| Blade Treatment | ${markdownValue(item.bladeTreatment)} |`,
      `| Handle Treatment | ${markdownValue(item.handleTreatment)} |`,
      `| Grind Angle | ${markdownValue(item.grindAngle == null ? null : `${item.grindAngle}°`)} |`,
      `| Blade Length | ${markdownValue(formatMeasurement(item.bladeLengthMm, unit))} |`,
      `| Blade Width | ${markdownValue(formatMeasurement(item.bladeWidthMm, unit))} |`,
      `| Handle Length | ${markdownValue(formatMeasurement(item.handleLengthMm, unit))} |`,
      `| Measurement Unit | ${markdownValue(unit)} |`,
      `| Purchase Price | ${markdownValue(item.purchasePrice)} |`,
      `| Purchase Date | ${markdownValue(item.purchaseDate)} |`,
      `| Purchase Date Precision | ${markdownValue(item.purchaseDateGranularity)} |`,
      `| Hand Forged | ${markdownValue(item.handForged)} |`,
      `| Daniel Forged | ${markdownValue(item.danielForged)} |`,
      `| Original Box | ${markdownValue(item.originalBox)} |`,
      `| Original Paperwork | ${markdownValue(item.originalPaperwork)} |`,
      `| Limited Edition | ${markdownValue(item.limitedEdition)} |`,
      `| Edition Number | ${markdownValue(item.editionNumber)} |`,
      `| Edition Total | ${markdownValue(item.editionTotal)} |`,
      `| Tomahawk / Axe | ${markdownValue(item.isAxe)} |`,
      `| Created | ${markdownValue(item.createdAt)} |`,
      `| Updated | ${markdownValue(item.updatedAt)} |`,
      "",
      "### Notes",
      "",
      `- **Blade Comments:** ${markdownValue(item.bladeComments ?? item.extendedDescription)}`,
      `- **Sheath Comments:** ${markdownValue(item.sheathComments)}`,
      `- **Extended Description:** ${markdownValue(item.extendedDescription)}`,
      `- **General Notes:** ${markdownValue(item.generalNotes)}`,
      "",
       "### Photos & Attachments",
      "",
    );

    if (item.photos.length === 0) {
      lines.push("_No attached photos._", "");
    } else {
      item.photos.forEach((photo) => {
        lines.push(
          `#### ${getPhotoRoleLabel(photo.role)}`,
          "",
       photo.mediaType === "application/pdf"
         ? `[${getPhotoRoleLabel(photo.role)} — ${photo.originalFilename ?? "PDF attachment"}](${photo.path}) (${photo.mediaType}, ${photo.byteSize ?? "unknown"} bytes)`
         : `![${item.name} — ${getPhotoRoleLabel(photo.role)}](${photo.path})`,
          "",
        );
      });
    }
  }

  return `${lines.join("\n")}\n`;
}

function buildReadme(createdAt: Date): string {
  return `# ForgeBook Portable Backup

Created: ${createdAt.toISOString()}

## Package layout

 - \`collection.md\` — a human-readable collection with relative links to every photo and document attachment, plus Set membership and order.
 - \`manifest.json\` — machine-readable item data, stable IDs, ordered Set child IDs, blade parent memberships, attachment roles, media types, original filenames, byte sizes, and relative paths.
- \`data.csv\` — the sanitized CSV data extract available in ForgeBook.
 - \`images/<entry-folder>/\` — original photo bytes, grouped by stable item ID and sanitized item name.
 - \`attachments/<entry-folder>/\` — original PDF attachment bytes.

## Import conventions

ForgeBook does not currently import backups. Future import tools should use each item's numeric \`id\` as its stable source identifier and resolve photo \`path\` values relative to the archive root. Filenames and folders are safe and deterministic; they do not contain credentials or depend on live ForgeBook URLs.
`;
}

export async function buildFullBackup(
  sourceItems: Item[],
  onProgress?: (progress: BackupProgress) => void,
): Promise<{ blob: Blob; filename: string }> {
  if (sourceItems.length === 0) {
    throw new Error(
      "Add at least one collection entry before creating a backup.",
    );
  }

  const createdAt = new Date();
  const items = [...sourceItems].sort((a, b) => a.id - b.id);
  const totalPhotos = items.reduce((sum, item) => sum + item.photos.length, 0);
  const entries: ZipEntry[] = [];
  const failures: BackupImageFailure[] = [];
  const manifestItems: ManifestItem[] = [];
  let completed = 0;

  onProgress?.({ stage: "preparing", completed: 0, total: totalPhotos });

  for (const item of items) {
    const folder = `item-${item.id}-${sanitizeBackupName(item.name)}`;
    const manifestPhotos: ManifestPhoto[] = [];
    const photos = [...item.photos].sort((a, b) => a.id - b.id);

    for (const photo of photos) {
      const label = `${item.name}: ${getPhotoRoleLabel(photo.role)}`;
      onProgress?.({
        stage: "images",
        completed,
        total: totalPhotos,
        currentLabel: label,
      });

      try {
        const response = await fetch(`/api/storage${photo.objectPath}`, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = new Uint8Array(await response.arrayBuffer());
        const mediaType = photo.mediaType ?? response.headers.get("content-type") ?? "image/jpeg";
        const extension = extensionFor(mediaType);
        const baseFolder = mediaType === "application/pdf" ? "attachments" : "images";
        const originalFilename = photo.originalFilename ?? `${sanitizeBackupName(photo.role)}-${photo.id}.${extension}`;
        const safeFilename = backupAttachmentFilename(originalFilename, photo.id, extension);
        const path = `${baseFolder}/${folder}/${safeFilename}`;
        entries.push({ name: path, data, crc: crc32(data) });
        manifestPhotos.push({
          id: photo.id,
          role: photo.role,
          path,
          mediaType,
          originalFilename: photo.originalFilename,
          byteSize: photo.byteSize ?? data.byteLength,
        });
      } catch (error) {
        failures.push({
          itemId: item.id,
          itemName: item.name,
          photoId: photo.id,
          role: photo.role,
          reason:
            error instanceof Error ? error.message : "Unknown download error",
        });
      } finally {
        completed += 1;
      }
    }

    manifestItems.push({
      ...item,
      photos: manifestPhotos,
      ...relationshipMetadata(item),
    });
  }

  if (failures.length > 0) {
    throw new BackupImageError(failures);
  }

  onProgress?.({
    stage: "archive",
    completed: totalPhotos,
    total: totalPhotos,
  });

  addTextEntry(entries, "README.md", buildReadme(createdAt));
  addTextEntry(
    entries,
    "collection.md",
    buildCollectionMarkdown(manifestItems, createdAt),
  );
  addTextEntry(entries, "data.csv", itemsToCsv(items));
  addTextEntry(
    entries,
    "manifest.json",
    `${JSON.stringify(
      {
        format: "forgebook-portable-backup",
        version: 1,
        createdAt: createdAt.toISOString(),
        itemCount: manifestItems.length,
        photoCount: totalPhotos,
        items: manifestItems,
      },
      null,
      2,
    )}\n`,
  );

  const blob = createZip(entries, createdAt);
  const date = createdAt.toISOString().slice(0, 10);
  return { blob, filename: `forgebook-backup-${date}.zip` };
}

export async function downloadFullBackup(
  items: Item[],
  onProgress?: (progress: BackupProgress) => void,
): Promise<string> {
  const result = await buildFullBackup(items, onProgress);
  onProgress?.({
    stage: "download",
    completed: items.reduce((sum, item) => sum + item.photos.length, 0),
    total: items.reduce((sum, item) => sum + item.photos.length, 0),
  });
  downloadBlob(result.blob, result.filename);
  return result.filename;
}
